// SelfAssessment — unified rubric + 4-tier scale for subjective questions.
//
// Per LLM_NATIVE_ARCHITECTURE.md §10 + 陛下 19AO:
//   "用户自评可以清晰的分几个级别，通用于简答，作文，essay，论述，推理等题型。"
//
// One scale, one rubric shape — used by:
//   - free_response.short_answer / calculation / translation
//   - essay.{user-written drafts}
//   - proof_drill.{user-derived attempts}
//   - any future subjective item type
//
// Why 4 tiers (0-3) and not 5 / 10 / 100:
//   - Granularity matches actual self-discrimination ability (research:
//     people can't reliably split "performance" beyond ~5 buckets)
//   - 4 forces a clear "above / below midpoint" decision (no fence-sitting at 3)
//   - Maps cleanly to letter grades the user already knows: 0=F · 1=C · 2=B · 3=A
//
// EPISTEMOLOGY: per陛下 19AN insight ("the application revolves around
// epistemology"), this is the place where the user's ENCODING of their own
// understanding meets the product's storage. The 4-tier scale + per-dimension
// rubric forces the user to decompose their judgment, which itself trains
// the metacognitive skill that drives exam performance. Score is a SIDE
// EFFECT; the act of self-assessment is the learning.
import { z } from 'zod';
/** The 4-tier self-assessment scale. Numeric so we can average / aggregate. */
export const SelfAssessmentScoreSchema = z.union([
    z.literal(0), // 未达成 — completely missed / wrong
    z.literal(1), // 部分达成 — direction right but execution flawed
    z.literal(2), // 基本达成 — main points covered, minor gaps
    z.literal(3), // 完全达成 — equivalent to or better than reference
]);
/** Static labels (zh) for UI. en label generated from value name. */
export const SELF_ASSESSMENT_LABELS_ZH = {
    0: '未达成',
    1: '部分达成',
    2: '基本达成',
    3: '完全达成',
};
export const SELF_ASSESSMENT_LABELS_EN = {
    0: 'Not met',
    1: 'Partial',
    2: 'Mostly met',
    3: 'Fully met',
};
/** A single rubric dimension the user judges themselves against.
 *
 *  Why per-dimension scoring (vs single 0-3 score):
 *   - Forces decomposition (content vs structure vs evidence vs language)
 *   - Surfaces specific weakness ("argument structure 1, content 3" → study
 *     structure)
 *   - Maps cleanly to future AI grader output (same dimensions)
 */
export const SelfAssessmentDimensionSchema = z.object({
    dimension: z.string().min(2).max(120)
        .describe('Short label for this rubric dimension (e.g. "argument structure", "use of evidence", "language fluency"). Max relaxed 2026-06-02 from 60 — LLM wrote dimension labels >60 chars for concept-heavy rubrics.'),
    criteria: z.string().min(15).max(300)
        .describe('Concrete criteria for full marks (score=3) on this dimension. Specific, falsifiable.'),
}).passthrough();
/** A user's actual self-assessment of one attempt. Persisted to IDB
 *  progress.subjectiveAttempts[itemId]. */
export const SelfAssessmentRecordSchema = z.object({
    /** Per-dimension scores. Keys MUST match the rubric dimensions. */
    scores: z.record(z.string(), SelfAssessmentScoreSchema),
    /** Optional free-form note (e.g. "missed the counter-argument"). */
    note: z.string().max(500).optional(),
    /** ISO datetime of this self-assessment. */
    at: z.string().datetime(),
}).passthrough();
/** Helper: average score across all dimensions of a record. Returns NaN when
 *  there are no scores yet. Used by stats / wrong-answers tracking. */
export function avgSelfScore(rec) {
    const values = Object.values(rec.scores);
    if (values.length === 0)
        return NaN;
    const sum = values.reduce((a, b) => a + b, 0);
    return sum / values.length;
}
/**
 * Threshold for "needs revisit" classification (subjective wrong-answers).
 *
 * Per陛下 19AO: "在错题本中应该有新的追踪形式和统计形式". Subjective
 * questions don't have binary right/wrong; we use average score < 2 as the
 * "needs revisit" signal (= less than 基本达成). 2.0 chosen because it's the
 * midpoint between partial and full mastery.
 */
export const SUBJECTIVE_REVISIT_THRESHOLD = 2.0;
//# sourceMappingURL=self-assessment.js.map